import { CostCenterAddressResponse } from 'b2b-common/core/account/api/costCenters/CostCenterAddresses.types';
import { OrderedProduct, OrderedProductItemNumbers, OrderedProductPricing } from 'b2b-common/core/OrderedProduct.types';
import { ProductSettingsType } from 'b2b-common/core/product/api/Product.types';
import { ProductBulkPricesType } from 'b2b-common/core/product/api/ProductBulkPrices.types';
import { COST_CENTER_ROLES } from 'b2b-common/core/utils/costCenters/roles';

export enum CART_ITEM_ERROR_TYPE {
  itemSoldOut = 'item_sold_out',
  maxOrderQuantityExceeded = 'max_order_quantity_exceeded',
  legacy = 'legacy',
  missingCertificate = 'missing_certificate',
  itemOutsideOfCoreAssortment = 'item_outside_of_core_assortment',
  itemMaxOrderQuantityExceeded = 'item_max_order_quantity_exceeded',
  priceChanged = 'price_changed',
  itemRemovedFromCart = 'item_removed_from_cart',
  configurationRequired = 'configuration_required',
  customerArticlePermissions = 'customer_article_permissions',
  unknownProductId = 'unknown_product_id',
}

export interface EclassVariant {
  version: '10' | '11' | '12' | '13' | '5' | '6' | '7' | '8' | '9';
  value: string;
}

export interface UnspscCodeVariant {
  version: '19' | '22' | '24';
  value: string;
}

export interface FetchCartItemResponseType extends OrderedProduct {
  name: string;
  coverLink: string;
  manufacturer: {
    id: string;
    name: string;
  };
  single: OrderedProductPricing & {
    discount: number;
    discountWithoutVat: number;
    final: number;
    finalWithoutVat: number;
  };
  total: OrderedProductPricing & {
    discount: number;
    discountWithoutVat: number;
    final: number;
    finalWithoutVat: number;
  };
  itemNumbers: OrderedProductItemNumbers;
  cart: {
    price: number;
    priceWithoutVat: number;
    quantity: number;
    reference: string;
  };
  expectedRedeliveryDate: string | null;
  errors: string[];
  hasBulkPrices: boolean;
  bulkPrices?: ProductBulkPricesType;
}

interface FetchCartRemovedItemResponseType {
  errors: string[];
  sku: string;
}

interface FetchCartSummaryResponseType {
  discount: number;
  discountVat: number;
  discountWithoutVat: number;
  bulkPricesDiscountWithVat: number;
  final: number;
  finalVat: number;
  finalWithoutVat: number;
  price: number;
  priceVat: number;
  priceWithoutVat: number;
  validated: number;
  voucher: {
    code: string | null;
    description: string | null;
    valid: number | null;
  };
  lockerId?: string | null;
  costCenter?: {
    uuid: string | null;
    isApprovalNeeded: boolean;
  };
}

export type FetchCartResponseType = {
  items: FetchCartItemResponseType[];
  removedItems: FetchCartRemovedItemResponseType[];
  summary: FetchCartSummaryResponseType;
};

export type CartProductDetailsType = {
  reference: string | null;
  quantity: number;
  price: number;
  priceWithoutVat: number;
}

export type CartProductAnalyticsType = {
  title: string;
  categories: string[];
}

export type CartProductSingleType = {
  currency: string;
  discount: number;
  discountWithoutVat: number;
  final: number;
  finalWithoutVat: number;
  price: number;
  priceWithoutVat: number;
}

export type CartProductTotalType = {
  currency: string;
  price: number;
  priceWithoutVat: number;
  discountWithoutVat: number;
  final: number;
  finalWithoutVat: number;
}

export type CartItemSupplierConfig = {
  partAuxiliaryId: string;
  productPreviewUrl?: string | null;
  layoutPreviewUrl?: string | null;
} | null;

export type CartProductType = {
  analytics: CartProductAnalyticsType;
  bulkPrice?: ProductBulkPricesType;
  cart: CartProductDetailsType;
  category: {
    name: string;
    url: string;
  };
  certificates?: string[];
  cover: string;
  coverUri: string;
  errors: string[];
  isActive: boolean;
  manufacturer: {
    id: string;
    name: string;
  };
  name: string;
  settings: ProductSettingsType;
  single: CartProductSingleType;
  sku: string;
  supplierConfig: CartItemSupplierConfig;
  total: CartProductTotalType;
};

export type CartSummaryCostCenter = {
  uuid: string | null;
  isApprovalNeeded: boolean;
}

export type CartSummaryType = {
  bulkDiscount: number;
  costCenter: CartSummaryCostCenter;
  discount: number;
  dropShipmentCosts?: number | null;
  final: number;
  finalVat: number;
  finalWithoutVat: number;
  isValidated: boolean;
  lockerId?: string | null;
  partialShippingCosts?: number | null;
  paymentCosts?: number | null;
  price: number;
  priceVat: number;
  priceWithoutVat: number;
  shippingCosts?: number;
  smallQuantitiesCost?: number | null;
  voucher: {
    code: string;
    description: string;
    isValid: boolean;
  } | null;
};

export type UpdateItemReference = {
  sku: string;
};

export type CartType = {
  products: CartProductType[];
  removedItemsSKUs: string[];
  summary: CartSummaryType;
};

export type AddItemToCartResponseType = {
  cart: {
    size: number;
    value: number;
  };
  item: {
    analytics: {
      categories: {
        depth: number;
        label: string;
        url: string;
      }[];
      title: string;
    };
    currentQuantity: number;
    errors: string[];
    insertedQuantity: number;
    maxAllowedQuantity: number;
    pricing: {
      list: {
        price: number;
        priceWithoutVat: number;
      };
      taxRate: string;
      user: {
        price: number;
        priceWithoutVat: number;
      };
    };
    productName: string;
  };
};

export type CartStatusWithAddedItemType = {
  cart: {
    count: number;
    value: number;
    lastInsertedQuantity: number | null;
    messages: string[],
    isLastInsertedNewItem?: boolean;
  },
  item: {
    price: number,
    priceWithoutVat: number,
    categories: string[],
    addedQuantity: number,
    currentQuantity: number,
    errors: string[],
    maxAllowedQuantity: number,
    productName: string,
  }
};

export type CartItemType = {
  quantity: number,
  reference: string
  sku: string,
  title: string
}

export type CartItemFailedType = CartItemType & {
  error: string;
};

export type AddItemsToCartResponseType = {
  added: CartItemType[],
  failed: CartItemFailedType[],
}

export type CartItemsErrorGroupType = {
  description?: CART_ITEM_ERROR_TYPE,
  items: CartItemFailedType[],
};

export type AddItemsToCartType = {
  added: CartItemType[],
  failed: CartItemsErrorGroupType[],
};

export type CartWishlistType = {
  id: string;
  name: string;
};

export type CartWishlistSearchType = CartWishlistType[];

export type FetchCartWishlistSearchResponseType = CartWishlistType[];

export type FetchCartStatusResponseType = {
  size: number;
  value: number;
};

export type CartStatusType = {
  count: number;
  value: number;
  lastInsertedQuantity: number | null;
  messages: string[],
  isLastInsertedNewItem?: boolean;
};

export type AddCartToWishlistResponseType = {
  id: string;
};

export type WishlistWithAddedCartType = {
  wishlistId: string;
};

export type CartCostCenterDetailsFirstMaster = {
  name: string;
  email: string;
  phone: number | null;
}

export type CartCostCenterDetailsResponse = {
  uuid: string;
  manuallyTypedId: string;
  name: string;
  description?: string;
  firstMaster?: CartCostCenterDetailsFirstMaster;
  userTotal: number;
  adminTotal: number;
  userCostCenterRole: COST_CENTER_ROLES
  deliveryAddresses?: CostCenterAddressResponse[];
  isReferenceRequired?: boolean;
  isDeliveryAddressRestricted?: boolean;
}

export type CartCostCenterDetailsAddress = {
  id: string;
  name: string;
  street: string;
  streetAddition: string;
  contactPerson: string;
  postCode: string;
  city: string;
  isDefault: boolean;
  deliveryNote: string | null;
}

export type CartCostCenterDetails = {
  id: string;
  label: string;
  name: string;
  description: string | null;
  firstMaster: CartCostCenterDetailsFirstMaster | null;
  userTotal: number;
  adminTotal: number;
  deliveryAddresses: CartCostCenterDetailsAddress[] | null;
  isReferenceRequired: boolean;
  isDeliveryAddressRestricted: boolean;
  userCostCenterRole: COST_CENTER_ROLES;
}
